import i18next, { type InitOptions, type TFunction, type i18n } from '@nerimity/i18lite';
import { createContext, createSignal, useContext, type ParentComponent } from 'solid-js';

export type TransProviderActions = {
  addResources(lng: string, ns: string, resources: any): i18n;
  changeLanguage(lng: string): Promise<void>;
  getI18next(): i18n;
};

const TransContext = createContext<[TFunction, TransProviderActions]>();

function createTransContext(instance: i18n, options: InitOptions): [TFunction, TransProviderActions] {
  const [language, setLanguage] = createSignal(null);

  instance.init(options, () => setLanguage(() => instance.language));

  async function changeLanguage(lng: string) {
    await instance.changeLanguage(lng);
    setLanguage(() => lng);
  }

  function addResources(
    lng: string,
    ns: string,
    resources: any,
    bundleOptions: { deep?: boolean; overwrite?: boolean } = {},
  ): i18n {
    return instance.addResourceBundle(lng, ns, resources, bundleOptions.deep, bundleOptions.overwrite);
  }

  const t = (...args: Parameters<TFunction>) => {
    // Access the signal to track it - this ensures components re-render when language changes
    language();
    return instance.t(...args);
  };

  return [
    t,
    {
      addResources,
      getI18next: () => instance,
      changeLanguage,
    },
  ];
}

export const useTransContext = () => useContext(TransContext);

export const TransProvider: ParentComponent<{ instance?: i18n; lng?: string; options?: InitOptions }> = (props) => {
  return (
    <TransContext.Provider
      value={createTransContext(props.instance || i18next, { lng: props.lng, ...props.options })}
      children={props.children}
    />
  );
};
